import { useState } from 'react'
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  Stack,
  Table,
  Text,
  Tooltip
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconMapPin, IconPlayerPause, IconPlayerPlay } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { useQueryCities } from 'hooks/cities'
import { useQueryImportBeats, useUpdateImportBeat } from 'hooks/importBeats'
import { useAppTranslation } from 'hooks/useTranslation'
import { ImportBeatRow } from 'supabase/importBeats'
import { AddBeatModal } from './AddBeatModal'
import { ChangeAreaModal } from './ChangeAreaModal'
import { areaLabels } from './areaLabels'
import { formatArea } from './beatArea'
import { mapBeatError } from './beatErrors'

/**
 * The Beats page (BEAT-01/02, D-13): every Facebook Page the importer reads,
 * with its area, and the only two edits an admin may make to one: pause/resume
 * and change area. A beat's URL and handle are never edited and a beat is never
 * removed (D-16): pausing is how a beat stops.
 *
 * Admin-only: the route sits inside <AdminRoute> and RLS on import_beats is
 * the real wall (0027/0028).
 */
export const Beats = () => {
  const { t } = useAppTranslation()
  const { isLoading, isError, error, beats, isShort } = useQueryImportBeats()
  const {
    isLoading: citiesLoading,
    isError: citiesError,
    error: citiesErrorValue,
    cities
  } = useQueryCities()
  const { setActive } = useUpdateImportBeat()

  const [addOpened, setAddOpened] = useState(false)
  // The beat stays set while the confirm modal closes, so its text does not
  // flip during the close transition.
  const [toggleOpened, setToggleOpened] = useState(false)
  const [toggleBeat, setToggleBeat] = useState<ImportBeatRow | null>(null)
  const [areaBeat, setAreaBeat] = useState<ImportBeatRow | null>(null)

  if (isError || citiesError) {
    return (
      <div>
        {t('common.error')}
        {(error ?? citiesErrorValue)?.message}
      </div>
    )
  }
  if (isLoading || citiesLoading) {
    return <div>{t('common.loading')}</div>
  }

  const cityList = cities ?? []
  const labels = areaLabels(t)
  const activeCount = beats.filter((beat) => beat.active).length

  const confirmToggle = async () => {
    if (!toggleBeat) return
    const beat = toggleBeat
    try {
      await setActive.mutateAsync({ handle: beat.handle, active: !beat.active })
      notifications.show({
        title: t('common.success'),
        message: t('beats.updated'),
        color: 'green'
      })
    } catch (err) {
      notifications.show({
        title: t('common.error'),
        message: t(mapBeatError(err).messageKey),
        color: 'red'
      })
    } finally {
      setToggleOpened(false)
    }
  }

  return (
    <Stack>
      <Group justify="space-between">
        <Group gap="md" align="baseline">
          <Text size="xl" fw={600}>
            {t('beats.title')}
          </Text>
          <Text c="dimmed">{t('beats.activeCount', { count: activeCount })}</Text>
        </Group>
        <Button onClick={() => setAddOpened(true)}>{t('beats.add')}</Button>
      </Group>

      {isShort && <Alert color="yellow">{t('beats.truncated')}</Alert>}

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t('beats.columns.handle')}</Table.Th>
            <Table.Th>{t('beats.columns.pageUrl')}</Table.Th>
            <Table.Th>{t('beats.columns.notes')}</Table.Th>
            <Table.Th>{t('beats.columns.area')}</Table.Th>
            <Table.Th>{t('beats.columns.status')}</Table.Th>
            <Table.Th>{t('beats.columns.updated')}</Table.Th>
            <Table.Th style={{ width: 100 }}>{t('common.actions')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {beats.map((beat) => (
            <Table.Tr key={beat.handle}>
              <Table.Td>{beat.handle}</Table.Td>
              <Table.Td style={{ wordBreak: 'break-all' }}>
                <a href={beat.page_url} target="_blank" rel="noopener noreferrer">
                  {beat.page_url}
                </a>
              </Table.Td>
              <Table.Td>{beat.notes ?? ''}</Table.Td>
              <Table.Td>{formatArea(beat, cityList, labels)}</Table.Td>
              <Table.Td>
                {beat.active ? (
                  <Badge color="green" style={{ width: 'max-content' }}>
                    {t('beats.active')}
                  </Badge>
                ) : (
                  <Badge color="gray" style={{ width: 'max-content' }}>
                    {t('beats.paused')}
                  </Badge>
                )}
              </Table.Td>
              <Table.Td>{dayjs(beat.updated_at).format('YYYY-MM-DD HH:mm')}</Table.Td>
              <Table.Td>
                <Group gap="xs" wrap="nowrap">
                  <Tooltip label={beat.active ? t('beats.pause') : t('beats.resume')}>
                    <ActionIcon
                      variant="subtle"
                      color={beat.active ? 'yellow' : 'green'}
                      aria-label={beat.active ? t('beats.pause') : t('beats.resume')}
                      onClick={() => {
                        setToggleBeat(beat)
                        setToggleOpened(true)
                      }}
                    >
                      {beat.active ? <IconPlayerPause size={16} /> : <IconPlayerPlay size={16} />}
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label={t('beats.changeArea')}>
                    <ActionIcon
                      variant="subtle"
                      color="blue"
                      aria-label={t('beats.changeArea')}
                      onClick={() => setAreaBeat(beat)}
                    >
                      <IconMapPin size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <Modal
        opened={toggleOpened}
        onClose={() => setToggleOpened(false)}
        title={t('common.confirm')}
      >
        <Stack>
          <Text>
            {toggleBeat?.active
              ? t('beats.confirmPause', { handle: toggleBeat?.handle })
              : t('beats.confirmResume', { handle: toggleBeat?.handle })}
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setToggleOpened(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              color={toggleBeat?.active ? 'yellow' : 'green'}
              onClick={confirmToggle}
              loading={setActive.isPending}
            >
              {toggleBeat?.active ? t('beats.pause') : t('beats.resume')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <AddBeatModal
        opened={addOpened}
        onClose={() => setAddOpened(false)}
        cities={cityList}
        beats={beats}
      />

      {areaBeat && (
        <ChangeAreaModal
          key={areaBeat.handle}
          beat={areaBeat}
          opened={areaBeat !== null}
          onClose={() => setAreaBeat(null)}
          cities={cityList}
        />
      )}
    </Stack>
  )
}
